import React, { useState, useEffect, useRef } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, Tag, Space,
    Tabs, Popconfirm, Tooltip, message, Card, Statistic, Row, Col
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    SaveOutlined, SendOutlined, CheckOutlined, FileTextOutlined, DownloadOutlined
} from '@ant-design/icons';
import ProgramsService from '../../services/ProgramsService';
import util from '../../utils/util';

const { TextArea } = Input;

const BANDS = {
    'Best Practice Champion': { color: '#16a34a', bg: '#f0fdf4', threshold: '≥85%' },
    'Strong Performer':       { color: '#2563eb', bg: '#eff6ff', threshold: '≥64%' },
    'Developing Performer':   { color: '#d97706', bg: '#fffbeb', threshold: '≥43%' },
    'Needs Support':          { color: '#dc2626', bg: '#fef2f2', threshold: '<43%' },
};

const SCORE_LABELS = {
    5: 'Excellent',
    4: 'Good',
    3: 'Adequate',
    2: 'Needs Improvement',
    1: 'Poor',
};

export default function QoCPrograms() {
    const [activeTab, setActiveTab]       = useState('registry');

    // Registry state
    const [programs, setPrograms]         = useState([]);
    const [haCategories, setHaCategories] = useState([]);
    const [pLoading, setPLoading]         = useState(false);
    const [pModal, setPModal]             = useState(false);
    const [editProg, setEditProg]         = useState(null);
    const [pForm]                         = Form.useForm();

    // Assessments state
    const [assessments, setAssessments]   = useState([]);
    const [aLoading, setALoading]         = useState(false);
    const [aModal, setAModal]             = useState(false);
    const [aForm]                         = Form.useForm();
    const [scoringModal, setScoringModal] = useState(false);
    const [currentAssessment, setCurrentAssessment] = useState(null);
    const [responses, setResponses]       = useState({});
    const [saving, setSaving]             = useState(false);
    const draftKey = currentAssessment ? `prog_draft_${currentAssessment.id}` : null;
    const [importing, setImporting]       = useState(false);
    const importRef                       = useRef(null);
    const [periods, setPeriods]           = useState([]);
    const [assessors, setAssessors]       = useState([]);
    const [progressFilter, setProgressFilter] = useState({ status: '', assessor_id: '', ha_category: '' });
    const [assignModal, setAssignModal]       = useState(false);
    const [returnModal, setReturnModal]       = useState(false);
    const [returnTarget, setReturnTarget]     = useState(null);
    const [returnComment, setReturnComment]   = useState('');
    const [assignProgram, setAssignProgram]   = useState(null);
    const [assignments, setAssignments]       = useState([]);
    const [allAssessors, setAllAssessors]     = useState([]);

    const isAdmin   = util.getModules()?.['prog_admin']  === 1 || util.getModules()?.['qoc_admin'] === 1;
    const canAssess = util.getModules()?.['prog_assess'] === 1 || isAdmin;
    const canApprove= util.getModules()?.['qoc_approve'] === 1 || isAdmin;

    useEffect(() => {
        loadPrograms();
        loadAssessments();
        loadSupportData();
        loadAllAssessors();
    }, []); // eslint-disable-line

    const loadAllAssessors = () => {
        import('../../utils/axios').then(({ default: axios }) => {
            axios.get('qoc/assessors')
                .then(r => setAllAssessors(r.data?.data || []))
                .catch(() => {});
        });
    };

    const loadSupportData = () => {
        // Load HA categories
        import('../../services/AYSService').then(({ default: AYSService }) => {
            AYSService.getHaCategories().then(r => setHaCategories(r.data?.data || []));
        });
        // Load periods
        import('../../services/QoCService').then(({ default: QoCService }) => {
            QoCService.getPeriods().then(r => {
                setPeriods((r.data?.data || []).filter(p => parseInt(p.is_active) === 1));
            });
            QoCService.getAssessors?.().then(r => setAssessors(r.data?.data || [])).catch(()=>{});
        });
    };

    // ── Programs Registry ─────────────────────────────────────────────────────
    const openAssignModal = (program) => {
        setAssignProgram(program);
        ProgramsService.getAssignments({ program_id: program.id })
            .then(r => setAssignments(r.data?.data || []))
            .catch(() => {});
        setAssignModal(true);
    };

    const loadPrograms = () => {
        setPLoading(true);
        ProgramsService.getPrograms()
            .then(r => setPrograms(r.data?.data || []))
            .catch(e => message.error(e.message))
            .finally(() => setPLoading(false));
    };

    const openPModal = (record = null) => {
        setEditProg(record);
        pForm.setFieldsValue(record
            ? { ...record, ha_category_id: record.ha_category_id ? parseInt(record.ha_category_id) : undefined }
            : { category: 'program', status: 1 }
        );
        setPModal(true);
    };

    const saveProgram = () => {
        pForm.validateFields().then(values => {
            util.showLoader();
            ProgramsService.saveProgram({ ...values, id: editProg?.id || 0 })
                .then(r => {
                    if (r.data?.code !== 200) throw new Error(r.data?.message);
                    message.success(r.data.message);
                    setPModal(false);
                    loadPrograms();
                })
                .catch(e => message.error(e.message))
                .finally(() => util.hideLoader());
        });
    };

    const deleteProgram = (id) => {
        util.showLoader();
        ProgramsService.deleteProgram(id)
            .then(() => { message.success('Program deleted'); loadPrograms(); })
            .catch(e => message.error(e.message))
            .finally(() => util.hideLoader());
    };

    const progCols = [
        { title: 'Name', dataIndex: 'name', render: (t, r) => (
            <div>
                <div style={{ fontWeight: 700 }}>{t}</div>
                <Tag style={{ fontSize: 10, marginTop: 2 }}>{r.code}</Tag>
                <Tag color="blue" style={{ fontSize: 10 }}>{r.category}</Tag>
            </div>
        )},
        { title: 'Award Category', dataIndex: 'ha_category_name', width: 200,
            render: v => v ? <Tag color="gold">{v}</Tag> : <span style={{ color: '#ccc' }}>Not set</span>
        },
        { title: 'Focal Person', dataIndex: 'focal_person', width: 160 },
        { title: 'Status', dataIndex: 'status', width: 80,
            render: v => <Tag color={v === '1' || v === 1 ? 'green' : 'red'}>{v === '1' || v === 1 ? 'Active' : 'Inactive'}</Tag>
        },
        { title: 'Assigned Assessors', dataIndex: 'assigned_assessors', width: 180,
            render: v => v ? <span style={{ fontSize: 12 }}>{v}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>None assigned</span>
        },
        { title: '', width: 160, render: (_, r) => (
            <Space>
                {isAdmin && <Tooltip title="Assign Assessors">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openPModal(r)} />
                </Tooltip>}
                {isAdmin && <Button size="small" type="dashed" onClick={() => openAssignModal(r)}>
                    Assign
                </Button>}
                {isAdmin && <Popconfirm title="Delete this program?" onConfirm={() => deleteProgram(r.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>}
            </Space>
        )},
    ];

    // ── Assessments ───────────────────────────────────────────────────────────
    const loadAssessments = () => {
        setALoading(true);
        ProgramsService.getAssessments()
            .then(r => setAssessments(r.data?.data || []))
            .catch(e => message.error(e.message))
            .finally(() => setALoading(false));
    };

    const openNewAssessment = () => {
        aForm.resetFields();
        aForm.setFieldsValue({
            assessment_date: new Date().toISOString().split('T')[0],
            period_label: 'ENHEA-2026',
        });
        setAModal(true);
    };

    const createAssessment = () => {
        aForm.validateFields().then(values => {
            util.showLoader();
            ProgramsService.saveAssessment(values)
                .then(r => {
                    if (r.data?.code !== 200) throw new Error(r.data?.message);
                    message.success(r.data.message);
                    setAModal(false);
                    loadAssessments();
                    // Open scoring immediately
                    openScoring(r.data.data.id);
                })
                .catch(e => message.error(e.message))
                .finally(() => util.hideLoader());
        });
    };

    const openScoring = async (assessmentId) => {
        util.showLoader();
        try {
            const res = await ProgramsService.getAssessmentDetail(assessmentId);
            const a   = res.data?.data;
            setCurrentAssessment(a);

            // Pre-fill responses from server
            const rMap = {};
            (a?.responses || []).forEach(r => {
                rMap[r.question_id] = {
                    score:          r.score ? parseInt(r.score) : null,
                    comment:        r.comment || '',
                    recommendation: r.recommendation || '',
                };
            });
            setResponses(rMap);
            // Check localStorage for unsaved draft
            restoreDraft(assessmentId, rMap);
            setScoringModal(true);
        } catch(e) {
            message.error(e.message);
        } finally {
            util.hideLoader();
        }
    };

    const saveResponses = async (andSubmit = false) => {
        setSaving(true);
        try {
            const payload = (currentAssessment?.responses || []).map(q => ({
                question_id:    q.question_id,
                score:          responses[q.question_id]?.score || null,
                comment:        responses[q.question_id]?.comment || '',
                recommendation: responses[q.question_id]?.recommendation || '',
            }));

            await ProgramsService.saveResponses({
                assessment_id: currentAssessment.id,
                responses: payload,
            });

            if (andSubmit) {
                await ProgramsService.submitAssessment(currentAssessment.id);
                clearDraft();
                message.success('Assessment submitted for approval');
                setScoringModal(false);
                loadAssessments();
            } else {
                clearDraft();
                message.success('Progress saved');
            }
        } catch(e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const [hasUnsaved, setHasUnsaved] = useState(false);
    useEffect(() => { if (!draftKey || Object.keys(responses).length === 0) return; try { localStorage.setItem(draftKey, JSON.stringify({ responses, savedAt: new Date().toISOString() })); setHasUnsaved(true); } catch(e) {} }, [responses]); // eslint-disable-line
    const clearDraft = () => { if (draftKey) { try { localStorage.removeItem(draftKey); } catch(e) {} setHasUnsaved(false); } };
    const restoreDraft = (assessmentId, existing) => { const key = 'prog_draft_' + assessmentId; try { const saved = localStorage.getItem(key); if (!saved) return; const { responses: draft, savedAt } = JSON.parse(saved); const hasChanges = Object.keys(draft).some(qId => { const d = draft[qId]; const e = existing[qId]; return !e || d.score !== e.score; }); if (!hasChanges) return; const mins = Math.round((Date.now() - new Date(savedAt)) / 60000); Modal.confirm({ title: 'Unsaved changes found', content: ('Restore changes from ' + (mins > 0 ? mins + ' min(s) ago' : 'just now') + '?'), okText: 'Restore', cancelText: 'Discard', onOk: () => setResponses(draft), onCancel: () => localStorage.removeItem(key) }); } catch(e) {} };
    const generateReport = async (assessmentId) => { util.showLoader(); try { const axiosMod = await import('../../utils/axios'); const ax = axiosMod.default; const res = await ax.get('programs/assessments/' + assessmentId + '/report/docx', { responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })); const a = document.createElement('a'); a.href = url; a.download = 'programs_report_' + assessmentId + '.docx'; a.click(); URL.revokeObjectURL(url); message.success('Report downloaded'); } catch(e) { message.error('Report download failed: ' + e.message); } finally { util.hideLoader(); } };
    const downloadBulkReports = async () => { util.showLoader(); try { const axiosMod = await import('../../utils/axios'); const ax = axiosMod.default; const periodLabel = periods.find(p => p.label.startsWith('ENHEA'))?.label || 'ENHEA-2026'; const res = await ax.get('programs/assessments/bulk-reports', { params: { period_label: periodLabel }, responseType: 'blob' }); const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' })); const a = document.createElement('a'); a.href = url; a.download = 'programs_reports_' + periodLabel + '.zip'; a.click(); URL.revokeObjectURL(url); message.success('All reports downloaded'); } catch(e) { message.error('Bulk download failed: ' + e.message); } finally { util.hideLoader(); } };
    const exportToExcel = async () => { try { const { exportProgramsExcel } = await import('../../utils/programsAssessmentExcel'); const sectionMap = {}; (currentAssessment?.responses || []).forEach(r => { const s = r.section_name || 'General'; if (!sectionMap[s]) sectionMap[s] = { name: s, items: [] }; sectionMap[s].items.push({ ...r, ...(responses[r.question_id] || {}) }); }); await exportProgramsExcel({ assessment: currentAssessment, sections: Object.values(sectionMap), programName: currentAssessment?.program_name, periodLabel: currentAssessment?.period_label }); message.success('Answer sheet downloaded'); } catch(e) { message.error('Export failed: ' + e.message); } };
    const handleImport = async (e) => { const file = e.target.files?.[0]; if (!file) return; setImporting(true); try { const { importProgramsExcel } = await import('../../utils/programsAssessmentExcel'); const { responses: imported, errors } = await importProgramsExcel(file); if (errors.length > 0) message.warning(errors.length + ' issue(s): ' + errors[0]); const nr = { ...responses }; imported.forEach(r => { nr[r.question_id] = { score: r.score, comment: r.comment, recommendation: r.recommendation }; }); setResponses(nr); message.success(imported.length + ' responses imported'); } catch(e) { message.error('Import failed: ' + e.message); } finally { setImporting(false); if(e.target) e.target.value = ''; } };

    const approveAssessment = async (id) => {
        util.showLoader();
        try {
            await ProgramsService.approveAssessment(id);
            message.success('Assessment approved');
            loadAssessments();
        } catch(e) {
            message.error(e.message);
        } finally {
            util.hideLoader();
        }
    };

    // Score summary
    const totalScore   = Object.values(responses).reduce((s, r) => s + (r.score || 0), 0);
    const answeredCount = Object.values(responses).filter(r => r.score).length;
    const totalQuestions = (currentAssessment?.responses || []).length || 14;
    const maxScore      = totalQuestions * 5;
    const scorePct      = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    const band = scorePct >= 85 ? 'Best Practice Champion'
               : scorePct >= 64 ? 'Strong Performer'
               : scorePct >= 43 ? 'Developing Performer'
               : 'Needs Support';
    const bandStyle = BANDS[band] || {};

    // Group responses by section for scoring form
    const sections = {};
    (currentAssessment?.responses || []).forEach(r => {
        const s = r.section_name || 'General';
        if (!sections[s]) sections[s] = [];
        sections[s].push(r);
    });

    const isDraft = currentAssessment?.status === 'draft' || currentAssessment?.status === 'revision';

    const aCols = [
        { title: 'Program', dataIndex: 'program_name', render: (t, r) => (
            <div>
                <div style={{ fontWeight: 700 }}>{t}</div>
                <Tag style={{ fontSize: 10 }}>{r.program_code}</Tag>
                <Tag color="blue" style={{ fontSize: 10 }}>{r.program_category}</Tag>
            </div>
        )},
        { title: 'Period', dataIndex: 'period_label', width: 120 },
        { title: 'Date', dataIndex: 'assessment_date', width: 110 },
        { title: 'Score', dataIndex: 'program_score', width: 80,
            render: (v, r) => v ? (
                <div>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>{v}</span>
                    <span style={{ color: '#888', fontSize: 11 }}>/70</span>
                </div>
            ) : <span style={{ color: '#ccc' }}>—</span>
        },
        { title: 'Band', dataIndex: 'program_band', width: 180,
            render: v => {
                if (!v) return <span style={{ color: '#ccc' }}>—</span>;
                const s = BANDS[v] || {};
                return <Tag style={{ color: s.color, background: s.bg, borderColor: s.color, fontWeight: 700 }}>{v}</Tag>;
            }
        },
        { title: 'Status', dataIndex: 'status', width: 110,
            render: v => {
                const cfg = {
                    draft:     { color: 'default',  label: 'Draft' },
                    submitted: { color: 'blue',     label: 'Submitted' },
                    approved:  { color: 'green',    label: 'Approved' },
                    revision:  { color: 'orange',   label: 'Revision' },
                };
                const c = cfg[v] || { color: 'default', label: v };
                return <Tag color={c.color}>{c.label}</Tag>;
            }
        },
        { title: '', width: 150, render: (_, r) => (
            <Space>
                {(r.status === 'draft' || r.status === 'revision') && canAssess && (
                    <Button size="small" type="primary" onClick={() => openScoring(r.id)}>
                        Continue
                    </Button>
                )}
                {r.status === 'approved' && (
                    <Button size="small" icon={<DownloadOutlined />}
                        onClick={() => generateReport(r.id)}>
                        Report
                    </Button>
                )}

            </Space>
        )},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>MoH Programs & Units</h4>
                    <small className="text-muted">Health Excellence Awards — Programs Assessment</small>
                </div>
            </div>

            <div className="page-pad">
                {/* Tab bar */}
                <div style={{ borderBottom: '1px solid #f0f0f0', marginBottom: 16, display: 'flex' }}>
                    {[
                        { key: 'registry',    label: `Programs Registry (${programs.length})` },
                        { key: 'assessments', label: `Assessments (${assessments.length})` },
                        { key: 'progress',    label: 'Progress Monitor' },
                        ...(canApprove ? [{ key: 'approval', label: `Approval Queue (${assessments.filter(a => a.status === 'submitted').length})` }] : []),
                    ].map(t => (
                        <div key={t.key} onClick={() => setActiveTab(t.key)} style={{
                            padding: '8px 20px', cursor: 'pointer',
                            fontWeight: activeTab === t.key ? 700 : 400,
                            color: activeTab === t.key ? '#1890ff' : '#595959',
                            borderBottom: activeTab === t.key ? '2px solid #1890ff' : '2px solid transparent',
                        }}>{t.label}</div>
                    ))}
                </div>

                {/* Registry panel */}
                {activeTab === 'registry' && (
                    <div>
                        <div className="d-flex mb-3">
                            {isAdmin && (
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => openPModal()}>
                                    Add Program / Unit
                                </Button>
                            )}
                        </div>
                        <Table rowKey="id" dataSource={programs} columns={progCols}
                            loading={pLoading} size="small" pagination={{ pageSize: 20 }} />
                    </div>
                )}

                {/* Assessments panel */}
                {activeTab === 'assessments' && (
                    <div>
                        <div className="d-flex mb-3">
                            {isAdmin && (
                                <Button type="primary" icon={<PlusOutlined />} onClick={openNewAssessment}>
                                    New Assessment
                                </Button>
                            )}
                            <Button icon={<DownloadOutlined />} onClick={downloadBulkReports}
                                style={{ marginLeft: 8 }}>
                                Download All Reports
                            </Button>
                        </div>
                        <Table rowKey="id" dataSource={assessments} columns={aCols}
                            loading={aLoading} size="small" pagination={{ pageSize: 20 }} />
                    </div>
                )}

                {/* Progress Monitor panel */}
                {activeTab === 'progress' && (
                    <div>
                        {/* Summary KPIs */}
                        <Row gutter={12} style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Programs', value: programs.length, color: '#1890ff' },
                                { label: 'Not Started', value: programs.filter(p => !assessments.find(a => String(a.program_id) === String(p.id))).length, color: '#9ca3af' },
                                { label: 'In Progress (Draft)', value: assessments.filter(a => a.status === 'draft').length, color: '#d97706' },
                                { label: 'Submitted', value: assessments.filter(a => a.status === 'submitted').length, color: '#2563eb' },
                                { label: 'Approved', value: assessments.filter(a => a.status === 'approved').length, color: '#16a34a' },
                            ].map(k => (
                                <Col key={k.label} xs={24} sm={12} md={5}>
                                    <Card size="small">
                                        <Statistic title={k.label} value={k.value}
                                            valueStyle={{ color: k.color, fontSize: 28, fontWeight: 800 }} />
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Filter bar */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                            <Select allowClear placeholder="Filter by status" style={{ width: 160 }}
                                value={progressFilter.status || undefined}
                                onChange={v => setProgressFilter(f => ({ ...f, status: v || '' }))}>
                                {['draft','submitted','approved','not started'].map(s => (
                                    <Select.Option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</Select.Option>
                                ))}
                            </Select>
                            <Select allowClear placeholder="Filter by category" style={{ width: 220 }}
                                value={progressFilter.ha_category || undefined}
                                onChange={v => setProgressFilter(f => ({ ...f, ha_category: v || '' }))}>
                                {haCategories.filter(c => ['PROGRAM','ADMIN_DEPT'].includes(c.code))
                                    .map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                            </Select>
                            <Button onClick={() => setProgressFilter({ status: '', assessor_id: '', ha_category: '' })}>
                                Clear
                            </Button>
                        </div>

                        {/* Progress table — one row per program */}
                        <Table
                            rowKey="id"
                            size="small"
                            pagination={{ pageSize: 20 }}
                            dataSource={programs.filter(p => {
                                const a = assessments.find(a => String(a.program_id) === String(p.id));
                                const status = a ? a.status : 'not started';
                                if (progressFilter.status && status !== progressFilter.status) return false;
                                if (progressFilter.ha_category && String(p.ha_category_id) !== String(progressFilter.ha_category)) return false;
                                return true;
                            })}
                            columns={[
                                { title: 'Program / Unit', dataIndex: 'name', render: (t, r) => (
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{t}</div>
                                        <Tag style={{ fontSize: 10 }}>{r.code}</Tag>
                                        <Tag color="blue" style={{ fontSize: 10 }}>{r.category}</Tag>
                                    </div>
                                )},
                                { title: 'Award Category', dataIndex: 'ha_category_name', width: 200,
                                    render: v => v ? <Tag color="gold">{v}</Tag> : <span style={{ color: '#ccc' }}>Not set</span>
                                },
                                { title: 'Status', width: 130, render: (_, r) => {
                                    const a = assessments.find(a => String(a.program_id) === String(r.id));
                                    if (!a) return <Tag color="default">Not Started</Tag>;
                                    const cfg = { draft: ['orange','In Progress'], submitted: ['blue','Submitted'], approved: ['green','Approved'], revision: ['red','Needs Revision'] };
                                    const [color, label] = cfg[a.status] || ['default', a.status];
                                    return <Tag color={color}>{label}</Tag>;
                                }},
                                { title: 'Assigned Assessors', width: 180, render: (_, r) => {
                                    return r.assigned_assessors
                                        ? <span style={{ fontSize: 12 }}>{r.assigned_assessors}</span>
                                        : <span style={{ color: '#ccc' }}>None assigned</span>;
                                }},
                                { title: 'Score', width: 100, render: (_, r) => {
                                    const a = assessments.find(a => String(a.program_id) === String(r.id));
                                    if (!a?.program_score) return <span style={{ color: '#ccc' }}>—</span>;
                                    return <span style={{ fontWeight: 800 }}>{a.program_score}/70</span>;
                                }},
                                { title: 'Band', width: 180, render: (_, r) => {
                                    const a = assessments.find(a => String(a.program_id) === String(r.id));
                                    if (!a?.program_band) return null;
                                    const s = BANDS[a.program_band] || {};
                                    return <Tag style={{ color: s.color, background: s.bg, borderColor: s.color, fontWeight: 700, fontSize: 11 }}>{a.program_band}</Tag>;
                                }},
                                { title: 'Last Updated', width: 130, render: (_, r) => {
                                    const a = assessments.find(a => String(a.program_id) === String(r.id));
                                    return a?.updated_at
                                        ? <span style={{ fontSize: 12, color: '#888' }}>{a.updated_at.split(' ')[0]}</span>
                                        : <span style={{ color: '#ccc' }}>—</span>;
                                }},
                                { title: '', width: 100, render: (_, r) => {
                                    const a = assessments.find(a => String(a.program_id) === String(r.id));
                                    if (!a) return canAssess ? (
                                        <Button size="small" type="primary" onClick={() => {
                                            aForm.setFieldsValue({ program_id: parseInt(r.id), period_label: 'ENHEA-2026', assessment_date: new Date().toISOString().split('T')[0] });
                                            setAModal(true);
                                        }}>Start</Button>
                                    ) : null;
                                    if (a.status === 'draft') return <Button size="small" onClick={() => openScoring(a.id)}>Continue</Button>;
                
                                    return null;
                                }},
                            ]}
                        />
                    </div>
                )}

                {/* Approval Queue panel */}
                {activeTab === 'approval' && canApprove && (
                    <div>
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff',
                            border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
                            Review submitted Programs assessments below. Open each to verify scores and findings before approving.
                        </div>
                        <Table
                            rowKey="id"
                            size="small"
                            pagination={{ pageSize: 20 }}
                            dataSource={assessments.filter(a => a.status === 'submitted')}
                            locale={{ emptyText: 'No assessments pending approval' }}
                            columns={[
                                { title: 'Program / Unit', dataIndex: 'program_name', render: (t, r) => (
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{t}</div>
                                        <Tag style={{ fontSize: 10 }}>{r.program_code}</Tag>
                                        <Tag color="blue" style={{ fontSize: 10 }}>{r.program_category}</Tag>
                                    </div>
                                )},
                                { title: 'Award Category', dataIndex: 'ha_category_name', width: 200,
                                    render: v => v ? <Tag color="gold">{v}</Tag> : <span style={{ color:'#ccc' }}>Not set</span>
                                },
                                { title: 'Assessor', dataIndex: 'assessor_name', width: 160 },
                                { title: 'Period', dataIndex: 'period_label', width: 120 },
                                { title: 'Date', dataIndex: 'assessment_date', width: 110 },
                                { title: 'Score', width: 90, render: (_, r) => r.program_score
                                    ? <span style={{ fontWeight: 800 }}>{r.program_score}/70</span>
                                    : <span style={{ color:'#ccc' }}>—</span>
                                },
                                { title: 'Band', width: 180, render: (_, r) => {
                                    if (!r.program_band) return null;
                                    const s = BANDS[r.program_band] || {};
                                    return <Tag style={{ color:s.color, background:s.bg, borderColor:s.color, fontWeight:700 }}>{r.program_band}</Tag>;
                                }},
                                { title: '', width: 180, render: (_, r) => (
                                    <Space>
                                        <Button size="small" icon={<FileTextOutlined />}
                                            onClick={() => openScoring(r.id)}>
                                            Review
                                        </Button>
                                        {r.status === 'approved' && (
                                            <Button size="small" icon={<DownloadOutlined />}
                                                onClick={() => generateReport(r.id)}>
                                                Report
                                            </Button>
                                        )}
                                        <Popconfirm
                                            title="Approve this assessment?"
                                            description="This will finalise the score and band."
                                            onConfirm={() => approveAssessment(r.id)}
                                            okText="Approve"
                                            okButtonProps={{ style: { background:'#16a34a', borderColor:'#16a34a' } }}>
                                            <Button size="small"
                                                style={{ background:'#16a34a', borderColor:'#16a34a', color:'#fff' }}
                                                icon={<CheckOutlined />}>
                                                Approve
                                            </Button>
                                        </Popconfirm>
                                        <Button size="small" danger onClick={() => {
                                            setReturnTarget(r);
                                            setReturnComment('');
                                            setReturnModal(true);
                                        }}>Return</Button>
                                    </Space>
                                )},
                            ]}
                        />
                    </div>
                )}

            </div>

            {/* Program modal */}
            <Modal title={editProg ? 'Edit Program' : 'Add Program / Unit'} open={pModal}
                onCancel={() => setPModal(false)} onOk={saveProgram} width={600}
                okText={<span><SaveOutlined /> Save</span>}>
                <Form form={pForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={16}>
                            <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. National Tuberculosis Programme" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Code" name="code" rules={[{ required: true }]}>
                                <Input placeholder="e.g. NTP" style={{ textTransform: 'uppercase' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item label="Type" name="category" rules={[{ required: true }]}>
                                <Select options={[
                                    { value: 'program',    label: 'Programme' },
                                    { value: 'unit',       label: 'Unit' },
                                    { value: 'department', label: 'Department' },
                                ]} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Award Category" name="ha_category_id">
                                <Select allowClear placeholder="Select category"
                                    options={haCategories
                                        .filter(c => ['PROGRAM','ADMIN_DEPT'].includes(c.code))
                                        .map(c => ({ value: parseInt(c.id), label: c.name }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Parent Unit / Directorate" name="parent_unit">
                        <Input placeholder="e.g. Directorate of Public Health" />
                    </Form.Item>
                    <Form.Item label="Description" name="description">
                        <TextArea rows={2} />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item label="Focal Person" name="focal_person">
                                <Input placeholder="Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Focal Email" name="focal_email">
                                <Input placeholder="email@gov.sz" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Status" name="status">
                        <Select options={[{ value: 1, label: 'Active' }, { value: 0, label: 'Inactive' }]} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* New assessment modal */}
            <Modal title="New Programs Assessment" open={aModal}
                onCancel={() => setAModal(false)} onOk={createAssessment}
                okText={<span><SendOutlined /> Start Assessment</span>}>
                <Form form={aForm} layout="vertical">
                    <Form.Item label="Program / Unit" name="program_id" rules={[{ required: true, message: 'Select a program' }]}>
                        <Select showSearch placeholder="Select program"
                            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                            options={programs.filter(p => p.status === '1' || p.status === 1)
                                .map(p => ({ value: parseInt(p.id), label: `${p.name} (${p.code})` }))} />
                    </Form.Item>
                    <Form.Item label="Assessment Period" name="period_label" rules={[{ required: true }]}>
                        <Select options={periods.map(p => ({ value: p.label, label: p.label }))} />
                    </Form.Item>
                    <Form.Item label="Assessment Date" name="assessment_date" rules={[{ required: true }]}>
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <TextArea rows={2} placeholder="Optional notes" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Scoring modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span>Programs Assessment — {currentAssessment?.program_name}</span>
                        {hasUnsaved && <Tag color="orange" style={{ fontSize: 11 }}>Unsaved changes</Tag>}
                        {answeredCount > 0 && (
                            <Tag style={{ color: bandStyle.color, background: bandStyle.bg, borderColor: bandStyle.color, fontWeight: 700 }}>
                                {totalScore}/{maxScore} ({scorePct}%) — {band}
                            </Tag>
                        )}
                    </div>
                }
                open={scoringModal}
                onCancel={() => {
                    if (hasUnsaved) {
                        Modal.confirm({
                            title: 'Unsaved changes',
                            content: 'You have unsaved changes. They will be preserved locally — click Save Progress before closing to sync to the server.',
                            okText: 'Close anyway',
                            cancelText: 'Stay',
                            onOk: () => { setScoringModal(false); loadAssessments(); },
                        });
                    } else {
                        setScoringModal(false);
                        loadAssessments();
                    }
                }}
                width={900}
                footer={
                    isDraft ? (
                        <Space wrap>
                            <Button onClick={() => { setScoringModal(false); loadAssessments(); }}>Close</Button>
                            <Button onClick={exportToExcel}>📥 Download Sheet</Button>
                            <Button loading={importing} onClick={() => importRef.current?.click()}>📤 Import Sheet</Button>
                            <input ref={importRef} type="file" accept=".xlsx" style={{ display:'none' }} onChange={handleImport} />
                            <Button icon={<SaveOutlined />} loading={saving} onClick={() => saveResponses(false)}>
                                Save Progress
                            </Button>
                            <Button type="primary" icon={<SendOutlined />} loading={saving}
                                disabled={answeredCount < totalQuestions}
                                onClick={() => saveResponses(true)}>
                                Submit for Approval
                            </Button>
                        </Space>
                    ) : (
                        <Button onClick={() => { setScoringModal(false); }}>Close</Button>
                    )
                }
            >
                {/* Scoring rubric */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                    {Object.entries(SCORE_LABELS).reverse().map(([score, label]) => (
                        <span key={score} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4,
                            background: '#f5f5f5', border: '1px solid #e5e7eb' }}>
                            <b>{score}</b> = {label}
                        </span>
                    ))}
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>
                        Total /70 · ≥60 = Best Practice Champion
                    </span>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${(answeredCount / totalQuestions) * 100}%`, height: '100%',
                            background: answeredCount === 14 ? '#16a34a' : '#1890ff', borderRadius: 4,
                            transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                        {answeredCount} of {totalQuestions} scored
                    </span>
                </div>

                {/* Questions by section */}
                {Object.entries(sections).map(([sectionName, qs]) => (
                    <div key={sectionName} style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af',
                            borderLeft: '3px solid #1e40af', paddingLeft: 10, marginBottom: 10 }}>
                            {sectionName}
                        </div>
                        {qs.map((q, qi) => {
                            const resp = responses[q.question_id] || {};
                            const scored = resp.score != null;
                            return (
                                <Card key={q.question_id} size="small" style={{
                                    marginBottom: 8,
                                    borderLeft: scored ? `3px solid #1890ff` : '3px solid #e5e7eb',
                                    background: scored ? '#f0f9ff' : '#fafafa',
                                    opacity: isDraft ? 1 : 0.8,
                                }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <span style={{ fontWeight: 700, color: '#6b7280', marginRight: 8 }}>
                                            {q.ref_no}.
                                        </span>
                                        <span style={{ fontSize: 13 }}>{q.question_text}</span>
                                    </div>
                                    <Row gutter={8}>
                                        <Col span={4}>
                                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Score (1-5)</div>
                                            <Select
                                                value={resp.score || undefined}
                                                disabled={!isDraft}
                                                style={{ width: '100%' }}
                                                placeholder="Rate"
                                                onChange={v => setResponses(prev => ({
                                                    ...prev,
                                                    [q.question_id]: { ...prev[q.question_id], score: v }
                                                }))}
                                                options={[5,4,3,2,1].map(s => ({
                                                    value: s,
                                                    label: `${s} — ${SCORE_LABELS[s]}`
                                                }))}
                                            />
                                        </Col>
                                        <Col span={10}>
                                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Key Findings / Evidence</div>
                                            <Input.TextArea
                                                value={resp.comment || ''}
                                                disabled={!isDraft}
                                                rows={2}
                                                placeholder="Describe the evidence observed..."
                                                onChange={e => setResponses(prev => ({
                                                    ...prev,
                                                    [q.question_id]: { ...prev[q.question_id], comment: e.target.value }
                                                }))}
                                            />
                                        </Col>
                                        <Col span={10}>
                                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                                                Recommendation {resp.score <= 2 && <Tag color="red" style={{ fontSize: 9 }}>Gap identified</Tag>}
                                            </div>
                                            <Input.TextArea
                                                value={resp.recommendation || ''}
                                                disabled={!isDraft}
                                                rows={2}
                                                placeholder={resp.score <= 2 ? 'Recommended action...' : 'Optional recommendation...'}
                                                onChange={e => setResponses(prev => ({
                                                    ...prev,
                                                    [q.question_id]: { ...prev[q.question_id], recommendation: e.target.value }
                                                }))}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            );
                        })}
                    </div>
                ))}
            </Modal>

            {/* Assessor Assignment Modal */}
            <Modal title={`Assign Assessors — ${assignProgram?.name}`}
                open={assignModal} onCancel={() => { setAssignModal(false); loadPrograms(); }}
                footer={<Button onClick={() => { setAssignModal(false); loadPrograms(); }}>Done</Button>}
                width={640}>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Add Assessors:</div>
                    <Space.Compact style={{ width: '100%' }}>
                        <Select mode="multiple" showSearch allowClear
                            placeholder="Select one or more assessors to add"
                            style={{ flex: 1 }}
                            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                            options={allAssessors
                                .filter(a => !assignments.find(x => String(x.assessor_id) === String(a.id)))
                                .map(a => ({ value: String(a.id), label: a.name || `User ${a.id}` }))}
                            onChange={v => window._selectedAssessors = v}
                        />
                        <Button type="primary" onClick={async () => {
                            const selected = window._selectedAssessors || [];
                            if (!selected.length) { message.warning('Select at least one assessor'); return; }
                            util.showLoader();
                            try {
                                await Promise.all(selected.map(assessorId =>
                                    ProgramsService.assignAssessor({
                                        program_id:   assignProgram.id,
                                        assessor_id:  assessorId,
                                        period_label: 'ENHEA-2026',
                                    })
                                ));
                                window._selectedAssessors = [];
                                const r = await ProgramsService.getAssignments({ program_id: assignProgram.id });
                                setAssignments(r.data?.data || []);
                                message.success(`${selected.length} assessor(s) assigned`);
                            } catch(e) { message.error(e.message); }
                            finally { util.hideLoader(); }
                        }}>Assign</Button>
                    </Space.Compact>
                </div>
                <Table rowKey="id" size="small" pagination={false}
                    dataSource={assignments}
                    locale={{ emptyText: 'No assessors assigned yet' }}
                    columns={[
                        { title: 'Assessor', dataIndex: 'assessor_name' },
                        { title: 'Period', dataIndex: 'period_label', width: 120 },
                        { title: 'Assigned', dataIndex: 'assigned_at', width: 120,
                            render: v => v?.split(' ')[0] },
                        { title: '', width: 80, render: (_, r) => (
                            <Popconfirm title="Remove this assessor?" onConfirm={async () => {
                                util.showLoader();
                                try {
                                    await ProgramsService.removeAssessor(r.id);
                                    const res = await ProgramsService.getAssignments({ program_id: assignProgram.id });
                                    setAssignments(res.data?.data || []);
                                    message.success('Assessor removed');
                                } catch(e) { message.error(e.message); }
                                finally { util.hideLoader(); }
                            }}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        )},
                    ]} />
            </Modal>

            {/* Return for Revision Modal */}
            <Modal
                title="Return Assessment for Revision"
                open={returnModal}
                onCancel={() => setReturnModal(false)}
                okText="Return for Revision"
                okButtonProps={{ danger: true }}
                onOk={async () => {
                    if (!returnComment.trim()) {
                        message.warning('Please provide a comment for the assessor');
                        return;
                    }
                    util.showLoader();
                    try {
                        await ProgramsService.returnForRevision(returnTarget.id, returnComment);
                        message.success('Assessment returned for revision');
                        setReturnModal(false);
                        setReturnComment('');
                        loadAssessments();
                    } catch(e) {
                        message.error(e.message);
                    } finally {
                        util.hideLoader();
                    }
                }}>
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Program: {returnTarget?.program_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                        The assessor will be notified to review and resubmit.
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        Comment / Reason for Return <span style={{ color: '#dc2626' }}>*</span>
                    </div>
                    <Input.TextArea
                        rows={4}
                        placeholder="Describe what needs to be corrected or clarified..."
                        value={returnComment}
                        onChange={e => setReturnComment(e.target.value)}
                    />
                </div>
            </Modal>
        </div>
    );
}
